import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as BorderIcon } from '../../../assets/icons/border-bottom.svg';
import { ReactComponent as LightningBoltIcon } from '../../../assets/icons/lightning-bolt-icon.svg';

interface MeterHardwareStatsProperties {
  /** Value between 0–100 */
  readonly value: number;
  /** Finish time label */
  readonly finishedTime?: string;
  /** Energy text like "22 kWh" */
  readonly energy?: string;
  completed?: string;
  finish?: string;
}

/**
 * EV Charging Gauge – Flutter Accurate Port
 */
function MeterHardwareStats({
  value,
  finishedTime = '',
  energy = '',
  completed = '',
  finish = '',
}: MeterHardwareStatsProperties): JSX.Element {
  const { t } = useTranslation('endConsumer');
  const [animated, setAnimated] = useState<number>(0);

  const safeValue = Math.min(100, Math.max(0, value));
  const progress = safeValue / 100;

  useEffect(() => {
    let frame: number;
    const start = animated;
    const end = progress;
    const duration = 800;
    const startTime = performance.now();

    /**
     * Animate function
     * @param time 4
     */
    const animate = (time: number): void => {
      const ter = Math.min((time - startTime) / duration, 1);
      const eased = ter * (2 - ter); // easeOut
      setAnimated(start + (end - start) * eased);
      if (ter < 1) frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [progress]);

  const arc = useMemo(() => {
    const size = 260;
    const center = size / 2;
    const radius = 100;

    const gap = 80;
    const total = 360 - gap;

    const startAngle = (170 - gap / 2) * (Math.PI / 180);
    const sweep = total * animated * (Math.PI / 180);

    const x1 = center + radius * Math.cos(startAngle);
    const y1 = center + radius * Math.sin(startAngle);

    const x2 = center + radius * Math.cos(startAngle + sweep);
    const y2 = center + radius * Math.sin(startAngle + sweep);

    const largeArc = sweep > Math.PI ? 1 : 0;

    return `
      M ${x1} ${y1}
      A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
    `;
  }, [animated]);

  return (
    <div className="meter-hardware-stats">
      <div className="meter-border "> </div>
      <BorderIcon className="icon" />
      <svg width="260" height="260" viewBox="0 0 260 260">
        <defs className="border-background">
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22c55e" />
            <stop offset="50%" stopColor="#eab308" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>

        {/* Outer thin green border */}

        {/* Grey track */}
        <circle
          cx="130"
          cy="130"
          r="100"
          stroke="#e5e7eb"
          strokeWidth="16"
          fill="none"
          strokeDasharray="470 135"
          transform="rotate(140 130 130)"
        />

        {/* Animated progress */}
        <path
          d={arc}
          stroke="url(#gaugeGradient)"
          strokeWidth="16"
          fill="none"
          strokeLinecap="round"
        />
      </svg>

      <div className="center">
        <LightningBoltIcon className="bolt" />
        <div className="value">{safeValue}%</div>
        <div className="label">{t(finish)}</div>
        <div className="time">{finishedTime}</div>
      </div>

      <div className="bottom">
        <span
          className={`badge ${completed === 'finished' || completed === 'Completed' ? 'completed' : 'in-progress'}`}>
          {t(completed)}
        </span>
        <div className="energy">{energy}</div>
      </div>
    </div>
  );
}

MeterHardwareStats.defaultProps = {
  finishedTime: '',
  energy: '',
  completed: 'finished',
  finish: 'finishTime',
};
export default MeterHardwareStats;
